import { useState } from 'react';
import { EvaluationResponses } from '../../../commonTypes/evaluation/evaluation.types';
import { EvaluationScoreValue } from '../../../commonTypes/evaluation/calculate.types';
import { Modal } from '../../Common/Modal';
import { Img } from '../../Common';

const scoreConfig = {
    [EvaluationScoreValue.NEGATIVE]: {
        label: 'Negative',
        badgeClass: 'evaluation-status-badge--negative',
    },
    [EvaluationScoreValue.NEUTRAL]: {
        label: 'Neutral',
        badgeClass: 'evaluation-status-badge--neutral',
    },
    [EvaluationScoreValue.POSITIVE]: {
        label: 'Positive',
        badgeClass: 'evaluation-status-badge--positive',
    },
};

const MAX_NOTE_LENGTH = 80;

export default function EvaluationTopicTable({ tableInfo }: { tableInfo: EvaluationResponses[] | undefined }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState<string>('');
    const [selectedTopic, setSelectedTopic] = useState<string>('');
    const [selectedScore, setSelectedScore] = useState<number>(0);

    const handlePreviewClick = (note: string, topic: string, score: number) => {
        setSelectedNote(note);
        setSelectedTopic(topic);
        setSelectedScore(score);
        setIsModalOpen(true);
    };

    const truncateNote = (note: string) => {
        if (note.length <= MAX_NOTE_LENGTH) {
            return note;
        }
        return `${note.substring(0, MAX_NOTE_LENGTH)}...`;
    };

    return (
        <div className="evaluation-topic-table">
            {tableInfo && tableInfo.length > 0 ? (
                <div className="evaluation-topic-table__list">
                    <div className="evaluation-topic-table__header">
                        <div className="evaluation-topic-table__header-cell">Topic</div>
                        <div className="evaluation-topic-table__header-cell">Evaluation</div>
                        <div className="evaluation-topic-table__header-cell">Note</div>
                        <div className="evaluation-topic-table__header-cell"></div>
                    </div>
                    {tableInfo.map(info => {
                        const config = scoreConfig[info.score as EvaluationScoreValue];
                        const hasLongNote = info.note && info.note.length > MAX_NOTE_LENGTH;
                        return (
                            <div key={info.topic + info.score} className="evaluation-topic-table__row">
                                <div className="evaluation-topic-table__topic">{info.topic}</div>
                                <div className="evaluation-topic-table__evaluation">
                                    {config ? (
                                        <div className={`evaluation-status-badge ${config.badgeClass}`}>{config.label}</div>
                                    ) : (
                                        <div className="evaluation-status-badge evaluation-status-badge--neutral">Not scored</div>
                                    )}
                                </div>
                                <div className="evaluation-topic-table__note">
                                    {info.note ? (
                                        <span>{truncateNote(info.note)}</span>
                                    ) : (
                                        <span className="evaluation-topic-table__note--empty">There is no note provided for this topic.</span>
                                    )}
                                </div>
                                <div className="evaluation-topic-table__actions">
                                    <Img
                                        onClick={() => handlePreviewClick(info.note!, info.topic, info.score)}
                                        className="evaluation-topic-table__preview-button"
                                        src="/svg/eye.svg"
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="evaluation-topic-table__no-data">
                    <h5>No data to display</h5>
                </div>
            )}

            <Modal isVisible={isModalOpen} closeModal={() => setIsModalOpen(false)}>
                <div className="evaluation-topic-table__modal-content">
                    <button className="evaluation-topic-table__modal-close" onClick={() => setIsModalOpen(false)}>
                        ×
                    </button>
                    <div className="evaluation-topic-table__modal-badge">
                        {scoreConfig[selectedScore as EvaluationScoreValue] ? (
                            <div className={`evaluation-status-badge ${scoreConfig[selectedScore as EvaluationScoreValue].badgeClass}`}>
                                {scoreConfig[selectedScore as EvaluationScoreValue].label}
                            </div>
                        ) : (
                            <div className="evaluation-status-badge evaluation-status-badge--neutral">Not scored</div>
                        )}
                    </div>
                    <div className="evaluation-topic-table__modal-topic">
                        <h2 className="evaluation-topic-table__modal-topic">{selectedTopic}</h2>
                    </div>
                    <div className="evaluation-topic-table__modal-note">
                        <p>{selectedNote}</p>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
